import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FilePutData, getData, postData, putData } from "../../../API";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";

function EditVerifiedReportForm() {
  const location = useLocation();
  const approvalData = location?.state?.details;
  const navigate = useNavigate();
  const [masterData, setMasterData] = useState({
    establishmentTypes: [],
    ownershipTypes: [],
    serviceStatuses: [],
    zones: [],
    wards: [],
    commercialTypes: [],
    rateCategories: [],
    rateSubCategories: [],
    filteredWards: [],
  });
  const [loading, setLoading] = useState(true);
  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    error: null,
  });

  // Initialize form initial values from approvalData
  const initialValues = {
    sdid: approvalData?.sdid || "",
    typeOfEstablishment: approvalData?.establishmentTypeId || "",
    ownershipType: approvalData?.ownerShipTypeId || "",
    serviceStatus: approvalData?.serviceStatusId || "",
    municipalRegNo: approvalData?.muncipalRegNo || "",
    municipalCommercialNo: approvalData?.municipalCommercialNo || "",
    registrationStartDate: approvalData?.registrationStartDate
      ? new Date(approvalData.registrationStartDate).toISOString().split("T")[0]
      : "",
    registrationEndDate: approvalData?.registrationEndDate
      ? new Date(approvalData.registrationEndDate).toISOString().split("T")[0]
      : "",
    propertyHouseNo: approvalData?.houseNo || "",
    ownerName: approvalData?.ownerName || "",
    ownerContactNo: approvalData?.ownerContactNo || "",
    emailId: approvalData?.emailId || "",
    minWasteQty: approvalData?.minWasteQty || "",
    commercialType: approvalData?.commercialTypeId || "",
    rateType: approvalData?.rateType || "",
    rate: approvalData?.displayRate || "",
    rateCategoryId: approvalData?.rateCategoryId || "",
    rateSubCategoryId: approvalData?.rateSubCategoryId || "",
    fixedValue: approvalData?.fixedValue || "",
    extraArea: approvalData?.extraArea || "",
    tenantName: approvalData?.tenantName || "",
    tenantContact: approvalData?.tenantContactNo || "",
    noOfMembers: approvalData?.noOfMembers || "",
    address: approvalData?.address || "",
    location:
      approvalData?.lat && approvalData?.lng
        ? `${approvalData.lat}, ${approvalData.lng}`
        : "",
    lat: approvalData?.lat || "",
    lng: approvalData?.lng || "",
    // landmark: approvalData?.landmark || "",
    remarks: approvalData?.remarks || "",
    zoneId: approvalData?.zoneId || "",
    zoneNo: approvalData.zoneNo || "",
    wardNo: approvalData?.wardNo || "",
    isCommercial: approvalData?.isCommercialRoomAttached || false,
    imageUrl: approvalData?.imageUrl || "",
    createdDate: approvalData?.createdDate
      ? new Date(approvalData.createdDate).toLocaleString()
      : "",
    createdBy: approvalData?.createdBy || "",
  };
  // Enhanced validation schema with comprehensive field validation
  const validationSchema = Yup.object({
    // Basic establishment information - all required
    typeOfEstablishment: Yup.string().nullable(),
    ownershipType: Yup.string().nullable(),
    serviceStatus: Yup.string().required("Service status is required"),
    municipalRegNo: Yup.string().nullable(),
    // Registration dates with proper validation
    registrationStartDate: Yup.date().typeError("Please enter a valid date"),
    registrationEndDate: Yup.date()
      .typeError("Please enter a valid date")
      .min(
        Yup.ref("registrationStartDate"),
        "End date cannot be before start date"
      ),

    // Property details - all required
    propertyHouseNo: Yup.string().required("Property House No is required"),

    // Owner information with format validation
    ownerName: Yup.string()
      .required("Owner name is required")
      .min(2, "Name must be at least 2 characters"),
    tenantContact: Yup.string()
      .nullable()
      .matches(
        /^[6-9][0-9]{9}$/,
        "Contact number must start with 6-9 and be exactly 10 digits"
      ),

    ownerContactNo: Yup.string()
      .nullable()
      .matches(
        /^[6-9][0-9]{9}$/,
        "Contact number must start with 6-9 and be exactly 10 digits"
      ),

    emailId: Yup.string()
      .nullable()
      .email("Invalid email format")
      .test(
        "not-just-number",
        "Invalid email address",
        (value) => !/^[0-9]+$/.test(value)
      ),

    // Waste management details
    minWasteQty: Yup.number()
      .typeError("Must be a number")
      .min(0, "Must be a positive number")
      .nullable(),

    rateCategoryId: Yup.string().required("Rate Category is required"),
    rateSubCategoryId: Yup.string().required("Rate Sub Category is required"),
    fixedValue: Yup.number()
      .nullable()
      .typeError("Fixed value must be a number")
      .min(0, "Fixed value must be a positive number"),
    extraArea: Yup.number()
      .nullable()
      .typeError("Extra area must be a number")
      .min(0, "Extra area must be a positive number"),

    // Tenant information - all required with format validation
    tenantName: Yup.string()
      .nullable()
      .min(2, "Name must be at least 2 characters"),
    noOfMembers: Yup.number()
      .required("Number of members is required")
      .integer("Must be a whole number")
      .typeError("Must be a number")
      .min(1, "Must have at least one member"),

    // Location details - all required with format validation
    address: Yup.string()
      .required("Address is required")
      .min(5, "Address must be at least 5 characters"),
    lat: Yup.number()
      .required("Latitude is required")
      .typeError("Latitude must be a number")
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90"),
    lng: Yup.number()
      .required("Longitude is required")
      .typeError("Longitude must be a number")
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180"),
    zoneNo: Yup.string().required("Zone is required"),
    wardNo: Yup.string().required("Ward is required"),

    // Boolean fields
    isCommercial: Yup.boolean().required("Please specify if commercial"),
    municipalCommercialNo: Yup.string().when("isCommercial", {
      is: true,
      then: (schema) => schema.required("Municipal Commercial No is required"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    // Conditional image validation - only required if no existing image
    imageUrl: Yup.mixed().test(
      "conditional-required",
      "Image is required",
      function (value) {
        // If there's an existing imageUrl as a string or a new file is uploaded, it's valid
        if ((typeof value === "string" && value) || value instanceof File) {
          return true;
        }
        // Otherwise, it's required
        return false;
      }
    ),
  });

  useEffect(() => {
    // Fetch master data from API
    const fetchMasterData = async () => {
      try {
        setLoading(true);
        const response = await getData("SurveyMasters/GetAllMaster");
        const response1 = await getData("SurveyMasters/CommercialTypes");
        // Process and organize the master data
        const zones = response.data.zone || [];
        const wards = response.data.wards || [];

        // Initialize filtered wards based on selected zone (if any)
        const filteredWards = initialValues.zone
          ? wards.filter((ward) => ward.ucczoneId == initialValues.zone)
          : [];

        setMasterData({
          establishmentTypes: response.data.estabishmentType || [],
          ownershipTypes: response.data.ownershipTypes || [],
          serviceStatuses: response.data.serviceStatus || [],
          zones: response.data.zone || [],
          wards: response.data.wards || [],
          commercialTypes: response1.data || [],
          rateCategories: response.data.rateCategory || [],
          rateSubCategories: response.data.rateSubCategory || [],
        });
      } catch (error) {
        console.error("Error fetching master data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();
  }, []);

  // Helper function to find name by id in master data arrays
  const findNameById = (array, idField, nameField, id) => {
    const item = array.find((item) => item[idField] == id);
    return item ? item[nameField] : null;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const establishmentType = findNameById(
        masterData.establishmentTypes,
        "establishmentTypeId",
        "establishmentTypeName",
        values.typeOfEstablishment
      );

      const ownerShipType = findNameById(
        masterData.ownershipTypes,
        "ownershipTypeId",
        "ownershipTypeName",
        values.ownershipType
      );

      const serviceStatusName = findNameById(
        masterData.serviceStatuses,
        "serviceStatusId",
        "serviceStatusName",
        values.serviceStatus
      );

      const commercialType = findNameById(
        masterData.commercialTypes,
        "commercialTypeId",
        "commercialTypeName",
        values.commercialType
      );

      const rateCategoryName = findNameById(
        masterData.rateCategories,
        "rateCategoryId",
        "rateCategoryName",
        values.rateCategoryId
      );

      const rateSubCategoryName = findNameById(
        masterData.rateSubCategories,
        "rateSubCategoryId",
        "subCategoryName",
        values.rateSubCategoryId
      );

      const zoneData = masterData.zones.find(
        (zone) => zone.uccZoneId == values.zone
      );
      const zoneName = zoneData ? zoneData.zoneName : null;

      const formData = new FormData();

      formData.append("sdid", values.sdid);
      formData.append("establishmentTypeId", values.typeOfEstablishment);
      formData.append("establishmentType", establishmentType);
      formData.append("ownerShipTypeId", values.ownershipType);
      formData.append("ownerShipType", ownerShipType);
      formData.append("serviceStatusId", values.serviceStatus);
      formData.append("serviceStatusName", serviceStatusName);
      formData.append("muncipalRegNo", values.municipalRegNo);
      formData.append("houseNo", values.propertyHouseNo);
      formData.append("ownerName", values.ownerName);
      formData.append("ownerContactNo", values.ownerContactNo);
      formData.append("commercialTypeId", values.commercialType);
      formData.append("commercialType", commercialType);
      formData.append("rateType", values.rateType);
      formData.append("tenantName", values.tenantName);
      formData.append("tenantContactNo", values.tenantContact);
      formData.append("address", values.address);
      formData.append("landmark", values.landmark);
      formData.append("ZoneId", values.zoneId);
      formData.append("ZoneNo", values.zoneNo);
      formData.append("WardNo", values.wardNo);
      formData.append("rateCategoryId", values.rateCategoryId);
      formData.append("rateCategoryName", rateCategoryName);
      formData.append("rateSubCategoryId", values.rateSubCategoryId);
      formData.append("rateSubCategoryName", rateSubCategoryName);
      formData.append("municipalCommercialNo", values.municipalCommercialNo);

      // formData.append("tagId", values.tagId || "");
      // formData.append("uuid", values.uuid || "");
     if (values.registrationStartDate) {
        formData.append("registrationStartDate", values.registrationStartDate);
      }
      formData.append("registrationEndDate", values.registrationEndDate || "");
      formData.append("minWasteQty", values.minWasteQty || "");
      formData.append("rate", values.rate || "");
      formData.append("noOfMembers", values.noOfMembers || "");
      formData.append("lat", values.lat || "");
      formData.append("lng", values.lng || "");
      formData.append("remarks", values.remarks || "");
      formData.append("extraArea", values.extraArea || "");
      formData.append("fixedValue", values.fixedValue || "");
      // formData.append("parentGuid", values.parentGuid || "");
      // formData.append("syncGuid", values.syncGuid || "");

      formData.append("isCommercialRoomAttached", values.isCommercial);
      // formData.append("isActive", values.isActive);

      if (values.imageUrl instanceof File) {
        formData.append("ImageUrl", values.imageUrl);
      } else if (typeof values.imageUrl === "string" && values.imageUrl) {
        formData.append("ImageUrl", values.imageUrl);
      } else {
        formData.append("ImageUrl", "");
      }

      const token = localStorage.getItem("token");

      const response = await axios.put(
        // "https://lsadevapi.vmaxtechservices.help/api/SurveyDetails/UpdateVerifiedRecords" ,
        "https://lsaapi.lucknowswachhata.com/api/SurveyDetails/UpdateVerifiedRecords",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Verified Report updated successfully");
        navigate("/module_reports");
      } else if (response.status === 409) {
        toast.error("You do not have permission to edit this record.");
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        toast.error("You do not have permission to edit this record.");
      } else if (error.response && error.response.status === 401) {
        toast.error("You don't have permission to edit this record.");
      } else {
        toast.error(
          `Failed to update survey details: ${error.message || "Unknown error"}`
        );
      }

      setSubmitStatus({
        loading: false,
        error: error.message || "Failed to update survey details",
      });
    } finally {
      setSubmitting(false);
      setSubmitStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "300px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <main className="page-content content">
        <div className="container-fluid p-0">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1 className="title_pages mb-0">Edit Verified Reports</h1>
            <Link
              to="/module_reports"
              className="btn btn-secondary"
              style={{ boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px" }}
            >
              <i className="fa-solid fa-arrow-left-long me-1"></i> Back
            </Link>
          </div>
          <div className="container mt-4">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnMount={true}
              validateOnBlur={true}
            >
              {({ values, handleChange, setFieldValue, isSubmitting }) => (
                <Form>
                  {/* Form fields - first section */}
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Type Of Establishment{" "}
                      </label>
                      <Field
                        as="select"
                        className={`form-select `}
                        name="typeOfEstablishment"
                      >
                        <option value="">Select Type</option>
                        {masterData.establishmentTypes.map((type) => (
                          <option
                            key={type.establishmentTypeId}
                            value={type.establishmentTypeId}
                          >
                            {type.establishmentTypeName}
                          </option>
                        ))}
                      </Field>
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Ownership Type</label>
                      <Field
                        as="select"
                        className={`form-select `}
                        name="ownershipType"
                      >
                        <option value="">Select Type</option>
                        {masterData.ownershipTypes.map((type) => (
                          <option
                            key={type.ownershipTypeId}
                            value={type.ownershipTypeId}
                          >
                            {type.ownershipTypeName}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="ownershipType"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Service Status <span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        className={`form-select `}
                        name="serviceStatus"
                      >
                        <option value="">Select Status</option>
                        {masterData.serviceStatuses.map((status) => (
                          <option
                            key={status.serviceStatusId}
                            value={status.serviceStatusId}
                          >
                            {status.serviceStatusName}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="serviceStatus"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Municipal Reg No</label>
                      <Field
                        type="text"
                        className={`form-control`}
                        name="municipalRegNo"
                      />
                    </div>
                    {/* Registration date fields */}
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Registration Start Date
                      </label>
                      <Field
                        type="date"
                        className={`form-control `}
                        name="registrationStartDate"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Registration End Date
                      </label>
                      <Field
                        type="date"
                        className={`form-control `}
                        name="registrationEndDate"
                      />
                    </div>

                    {/* Owner details fields */}
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Property House No <span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="propertyHouseNo"
                      />
                      <ErrorMessage
                        name="propertyHouseNo"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Owner Name <span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="ownerName"
                      />
                      <ErrorMessage
                        name="ownerName"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Owner Contact No</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="ownerContactNo"
                        maxLength={10}
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Email ID</label>
                      <Field
                        type="text"
                        className={`form-control`}
                        name="emailId"
                      />
                    </div>

                    {/* Commercial/Rate fields */}
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Min Waste Qty</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="minWasteQty"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Commercial Type</label>
                      <Field
                        as="select"
                        className={`form-select `}
                        name="commercialType"
                      >
                        <option value="">Select Type</option>
                        {masterData.commercialTypes.map((type) => (
                          <option
                            key={type.commercialTypeId}
                            value={type.commercialTypeId}
                          >
                            {type.commercialTypeName}
                          </option>
                        ))}
                      </Field>
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        className={`form-select `}
                        name="rateCategoryId"
                        onChange={(e) => {
                          handleChange(e);
                          // Reset sub category when category changes
                          setFieldValue("rateSubCategoryId", "");
                        }}
                      >
                        <option value="">Select Category</option>
                        {masterData.rateCategories.map((category) => (
                          <option
                            key={category.rateCategoryId}
                            value={category.rateCategoryId}
                          >
                            {category.rateCategoryName}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="rateCategoryId"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Sub Category <span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        className={`form-select`}
                        name="rateSubCategoryId"
                        disabled={!values.rateCategoryId}
                      >
                        <option value="">Select Sub Category</option>
                        {masterData.rateSubCategories
                          .filter(
                            (subCategory) =>
                              !values.rateCategoryId ||
                              subCategory.rateCategoryId ===
                                parseInt(values.rateCategoryId)
                          )
                          .map((subCategory) => (
                            <option
                              key={subCategory.rateSubCategoryId}
                              value={subCategory.rateSubCategoryId}
                            >
                              {subCategory.subCategoryName}
                            </option>
                          ))}
                      </Field>
                      <ErrorMessage
                        name="rateSubCategoryId"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Rate Type</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="rateType"
                      />
                      <ErrorMessage
                        name="rateType"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Rate</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="rate"
                      />
                      <ErrorMessage
                        name="rate"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Fixed Value</label>
                      <Field
                        type="text"
                        // className="form-control"
                        className={`form-control `}
                        name="fixedValue"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Extra Area</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="extraArea"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Tenant Name</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="tenantName"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Tenant Contact</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="tenantContact"
                        maxLength={10}
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        No Of Family Members
                        <span className="text-danger">*</span>
                      </label>
                      <Field
                        type="number"
                        className={`form-control `}
                        name="noOfMembers"
                      />
                      <ErrorMessage
                        name="noOfMembers"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Address <span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="address"
                      />
                      <ErrorMessage
                        name="address"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Latitude<span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        className={`form-control`}
                        name="lat"
                      />
                      <ErrorMessage
                        name="lat"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Longitude<span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        className={`form-control`}
                        name="lng"
                      />
                      <ErrorMessage
                        name="lng"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Landmark</label>
                      <Field
                        type="text"
                        className={`form-control`}
                        name="landmark"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">Remarks</label>
                      <Field
                        type="text"
                        className={`form-control `}
                        name="remarks"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Zone<span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        className="form-select"
                        name="zoneId"
                        value={values.zoneId}
                        onChange={(e) => {
                          const selectedZoneId = e.target.value;
                          const selectedZone = masterData.zones.find(
                            (zone) => zone.uccZoneId == selectedZoneId
                          );
                          handleChange(e);
                          setFieldValue("wardNo", ""); // Clear ward
                          setFieldValue(
                            "zoneNo",
                            selectedZone?.uccZoneId || ""
                          );
                        }}
                      >
                        <option value="">Select Zone</option>
                        {masterData.zones.map((zone) => (
                          <option key={zone.uccZoneId} value={zone.uccZoneId}>
                            {zone.zoneName}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="zoneNo"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    {/* Display the actual wardNo value from the response */}
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Ward<span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        className="form-select"
                        name="wardNo"
                        disabled={!values.zoneId}
                      >
                        <option value="">Select Ward</option>
                        {masterData.wards
                          .filter((ward) => ward.ucczoneId == values.zoneId)
                          .map((ward) => (
                            <option key={ward.wardId} value={ward.wardNo}>
                              {`${ward.wardNo} - ${ward.wardName}`}
                            </option>
                          ))}
                      </Field>
                      <ErrorMessage
                        name="wardNo"
                        component="div"
                        className="text-danger"
                      />
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Commercial Room Attached
                        <span className="text-danger">*</span>
                      </label>
                      <Field name="isCommercial">
                        {({ field, form }) => (
                          <select
                            className="form-select"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value === "true"; // Convert string to boolean
                              form.setFieldValue("isCommercial", value);
                            }}
                          >
                            <option value="">Select</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        )}
                      </Field>
                      <ErrorMessage
                        name="isCommercial"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    {values.isCommercial === true && (
                      <div className="col-md-3 mb-3">
                        <label className="form-label">
                          Municipal Commercial No{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          className="form-control"
                          name="municipalCommercialNo"
                        />
                        <ErrorMessage
                          name="municipalCommercialNo"
                          component="div"
                          className="text-danger"
                        />
                      </div>
                    )}
                    {/* Image URL and Preview */}
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Image<span className="text-danger">*</span>
                      </label>
                      <div className="d-flex gap-3 flex-column">
                        {/* Show current image if available */}
                        {typeof values.imageUrl === "string" &&
                          values.imageUrl && (
                            <div className="mt-2">
                              <img
                                src={values.imageUrl}
                                alt="Property"
                                className="img-thumbnail"
                                style={{ maxHeight: "100px" }}
                              />
                            </div>
                          )}

                        {/* File input for uploading new image */}
                        <div>
                          <input
                            type="file"
                            className={`form-control`}
                            onChange={(event) => {
                              const file = event.currentTarget.files[0];
                              if (file) {
                                setFieldValue("imageUrl", file);
                              }
                            }}
                          />
                          <small className="form-text text-muted">
                            Upload a new image or keep the existing one
                          </small>
                          <ErrorMessage
                            name="imageUrl"
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-3 mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </main>
    </div>
  );
}

export default EditVerifiedReportForm;
